<template>
  <component
    :is="component"
    type="button"
    :class="[{ disabled: disabled }, `theme-${theme}`]"
    class="button-action"
    v-bind="props"
    @click="handleBaseClick"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
  >
    <span v-if="autoPlayProgress > 0" class="progress" :style="styleProgress" />
    <span v-if="$slots.icon" class="icon"><slot name="icon" /></span>
    <span v-if="$slots.default" class="content"><slot /></span>
  </component>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router';
import type { NuxtLinkProps } from '#app';

export interface ButtonCardProps extends NuxtLinkProps {
  disabled?: boolean;
  to?: string | RouteLocationRaw;
  autoPlayProgress?: number;
  theme?: 'default' | 'secondary';
}
const props = defineProps({
  disabled: { type: Boolean },
  to: null,
  autoPlayProgress: { default: 0 },
  theme: { default: 'default' }
});

const emit = defineEmits(["timerAutoPlayComplete"]);

const component = computed(() => {
  if (props.to || props.href) {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});

const activeElement = useActiveElement();

const handleBaseClick = () => {
  if (activeElement.value) activeElement.value.blur();
};

const handleMouseLeave = () => {};

const handleMouseOver = () => {};

const styleProgress = computed(() => {
  const percent = -Math.min(props.autoPlayProgress * 100, 100);
  return {
    transform: `translateX(${percent}%)`,
  };
});

watch(
  () => props.autoPlayProgress,
  (value) => {
    if (value === 0) {
      handleAutoPlay();
    }
  }
);

const handleAutoPlay = () => {
  if (props.to) navigateTo(props.to);
};
</script>
<style lang="scss" scoped>
.button-action {
  @include stroke('m', 'm');

  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--gap_n);
  overflow: hidden;
  border-color: var(--color_secondary_lighter);

  &.theme-secondary {
    border-color: var(--color_secondary_light);
    color: var(--color_secondary);
  }

  &::before,
  .progress {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    transition: transform 300ms $ease-in-quad;
    background-color: var(--color_secondary_lighter);
  }

  .progress {
    transition: transform 300ms linear;
  }

  .icon,
  .content {
    z-index: 1;
  }

  @include hover-focus-desktop {
    &::before {
      transform: translateX(0);
      transition: transform 300ms $ease-in-out-quad;
      opacity: 1;
    }
  }

  &.active,
  &.disabled,
  &.router-link-exact-active {
    cursor: auto;
  }
}
</style>
